import { useCallback } from 'react';
import { useToast } from 'hooks/useToast';

import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import type { Condition } from 'types/notification';
import * as notificationApi from 'api/notificationApi';

import {
  editActions,
  editSelectors,
} from 'store/reducers/notificationReducer/editSlice';
import {
  listActions,
  listSelectors,
} from 'store/reducers/notificationReducer/listSlice';
import { customersSelectors } from 'store/reducers/notificationReducer/customersSlice';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { NotificationFormFields } from '../types/notificationFormField';

/**
 * お知らせ更新・削除 store の実装をコンポーネントから隠すための中間層
 * @returns お知らせ更新・削除 store の selector のラッパーと action dispatch 処理 のラッパー
 */
export const useEditStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const isOpenInputModal = useAppSelector(editSelectors.isOpenInputModal);
  const isOpenConfirmationModal = useAppSelector(
    editSelectors.isOpenConfirmationModal,
  );
  const isOpenDeleteModal = useAppSelector(editSelectors.isOpenDeleteModal);
  const title = useAppSelector(editSelectors.title);
  const message = useAppSelector(editSelectors.message);
  const isHigh = useAppSelector(editSelectors.isHigh);
  const startDate = useAppSelector(editSelectors.startDate);
  const endDate = useAppSelector(editSelectors.endDate);
  const condition = useAppSelector(editSelectors.condition);
  const conditionForDisplay = useAppSelector(editSelectors.conditionForDisplay);
  const shouldShowCustomerList = useAppSelector(
    editSelectors.shouldShowCustomerList,
  );

  // postNotification 用
  const selectedCustomers = useAppSelector(
    customersSelectors.selectedCustomers,
  );

  // deleteNotification 用
  const selectedNotification = useAppSelector(
    listSelectors.selectedNotification,
  );

  const init = useCallback(() => {
    dispatch(editActions.init());
  }, [dispatch]);

  const openModal = useCallback(() => {
    dispatch(editActions.modalOpened());
  }, [dispatch]);

  const goConfirmationModal = useCallback(() => {
    dispatch(editActions.stepMovedToConfirmation());
  }, [dispatch]);

  const goInputModal = useCallback(() => {
    dispatch(editActions.stepMovedToInput());
  }, [dispatch]);

  const goDeleteModal = useCallback(() => {
    dispatch(editActions.stepMovedToDelete());
  }, [dispatch]);

  const setCondition = useCallback(
    (_condition: Condition) => {
      dispatch(
        editActions.conditionUpdated({
          condition: _condition,
        }),
      );
    },
    [dispatch],
  );

  const setForm = useCallback(
    (form: NotificationFormFields) => {
      dispatch(
        editActions.formGroupUpdated({
          form,
        }),
      );
    },
    [dispatch],
  );

  const putNotificationAndReload = useCallback(async () => {
    if (!selectedNotification) {
      return;
    }
    dispatch(loaderActions.setLoading(true));
    const params = notificationApi.createPutNotificationParams(
      title,
      message,
      isHigh,
      startDate,
      endDate,
      condition,
      selectedCustomers,
    );
    const result = await notificationApi.putNotification(
      selectedNotification.id,
      params,
    );
    if (result.ok) {
      // お知らせ一覧をリロードするために最終更新日を更新する
      dispatch(
        listActions.finalUpdateTimeUpdated({
          finalUpdateTime: new Date().toISOString(),
        }),
      );
      toast({
        status: 'success',
        description: 'お知らせの更新に成功しました。',
      });
    } else {
      toast({
        status: 'error',
        description: 'お知らせ更新の失敗しました。',
      });
    }
    dispatch(loaderActions.setLoading(false));
  }, [
    selectedNotification,
    title,
    message,
    isHigh,
    startDate,
    endDate,
    condition,
    selectedCustomers,
    toast,
    dispatch,
  ]);

  const deleteNotificationAndReload = useCallback(async () => {
    if (!selectedNotification) {
      return;
    }
    dispatch(loaderActions.setLoading(true));
    const result = await notificationApi.deleteNotification(
      selectedNotification.id,
    );
    if (result.ok) {
      // お知らせ一覧をリロードするために最終更新日を更新する
      dispatch(
        listActions.finalUpdateTimeUpdated({
          finalUpdateTime: new Date().toISOString(),
        }),
      );
      toast({
        status: 'success',
        description: 'お知らせの削除に成功しました。',
      });
    } else {
      toast({
        status: 'error',
        description: 'お知らせ削除の失敗しました。',
      });
    }
    dispatch(loaderActions.setLoading(false));
  }, [selectedNotification, toast, dispatch]);

  return {
    isOpenInputModal,
    isOpenConfirmationModal,
    isOpenDeleteModal,
    title,
    message,
    isHigh,
    startDate,
    endDate,
    condition,
    conditionForDisplay,
    shouldShowCustomerList,
    init,
    openModal,
    goConfirmationModal,
    goInputModal,
    goDeleteModal,
    setCondition,
    setForm,
    putNotificationAndReload,
    deleteNotificationAndReload,
  };
};
